/**
 * Clinical AI Agent - Assessments Tab (Pain & Infection sub-tabs)
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Heart, Thermometer, Loader2, CheckCircle, AlertTriangle
} from "lucide-react";
import { 
  PAIN_QUALITIES, INFECTION_SYMPTOMS, URINARY_SYMPTOMS, 
  RESPIRATORY_SYMPTOMS, getSeverityClass 
} from "./constants";

export function PainAssessmentForm({
  painForm,
  setPainForm,
  loading,
  isDark,
  onSubmit
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Pain Location</Label>
          <Input
            placeholder="e.g., lower back, abdomen"
            value={painForm.pain_location}
            onChange={(e) => setPainForm({ ...painForm, pain_location: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
            data-testid="pain-location-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Pain Quality</Label>
          <Select
            value={painForm.pain_quality}
            onValueChange={(v) => setPainForm({ ...painForm, pain_quality: v })}
          >
            <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAIN_QUALITIES.map((q) => (
                <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Pain Intensity (0-10)</Label>
        <div className="flex items-center gap-4">
          <Slider
            value={[painForm.pain_intensity]}
            onValueChange={([v]) => setPainForm({ ...painForm, pain_intensity: v })}
            max={10}
            step={1}
            className="flex-1"
            data-testid="pain-intensity-slider"
          />
          <span className={`text-2xl font-bold w-12 text-center ${
            painForm.pain_intensity >= 7 ? "text-red-400" :
            painForm.pain_intensity >= 4 ? "text-yellow-400" : "text-green-400"
          }`}>
            {painForm.pain_intensity}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Aggravating Factors (comma-separated)</Label>
        <Input
          placeholder="movement, pressure, coughing"
          value={painForm.aggravating_factors}
          onChange={(e) => setPainForm({ ...painForm, aggravating_factors: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Relieving Factors (comma-separated)</Label>
        <Input
          placeholder="rest, positioning, medication"
          value={painForm.relieving_factors}
          onChange={(e) => setPainForm({ ...painForm, relieving_factors: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Impact on Function</Label>
          <Input
            placeholder="Unable to ambulate..."
            value={painForm.impact_function}
            onChange={(e) => setPainForm({ ...painForm, impact_function: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Impact on Sleep</Label>
          <Input
            placeholder="Wakes frequently..."
            value={painForm.impact_sleep}
            onChange={(e) => setPainForm({ ...painForm, impact_sleep: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">PRN Use (Last 24h)</Label>
        <Input
          type="number"
          min="0"
          value={painForm.prn_use_last_24h}
          onChange={(e) => setPainForm({ ...painForm, prn_use_last_24h: parseInt(e.target.value) || 0 })}
          className={`w-24 ${isDark ? "bg-slate-700 border-slate-600" : ""}`}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Clinical Notes</Label>
        <Textarea
          placeholder="Additional clinical observations..."
          value={painForm.clinical_notes}
          onChange={(e) => setPainForm({ ...painForm, clinical_notes: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={3}
          data-testid="pain-clinical-notes"
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading || !painForm.pain_location}
        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600"
        data-testid="submit-pain-assessment-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Heart className="h-4 w-4 mr-2" />}
        Analyze Pain Assessment
      </Button>
    </div>
  );
}

export function InfectionAssessmentForm({
  infectionForm,
  setInfectionForm,
  loading,
  isDark,
  onSubmit
}) {
  const toggleSymptom = (symptom, list, setList) => {
    if (list.includes(symptom)) {
      setList(list.filter(s => s !== symptom));
    } else {
      setList([...list, symptom]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Signs & Symptoms</Label>
        <div className="grid grid-cols-2 gap-2">
          {INFECTION_SYMPTOMS.map((symptom) => (
            <div key={symptom} className="flex items-center space-x-2">
              <Checkbox
                id={`symptom-${symptom}`}
                checked={infectionForm.signs_symptoms.includes(symptom)}
                onCheckedChange={() => {
                  if (infectionForm.signs_symptoms.includes(symptom)) {
                    setInfectionForm({
                      ...infectionForm,
                      signs_symptoms: infectionForm.signs_symptoms.filter(s => s !== symptom)
                    });
                  } else {
                    setInfectionForm({
                      ...infectionForm,
                      signs_symptoms: [...infectionForm.signs_symptoms, symptom]
                    });
                  }
                }}
              />
              <label htmlFor={`symptom-${symptom}`} className="text-xs">{symptom}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Temp (°F)</Label>
          <Input
            type="number"
            step="0.1"
            placeholder="98.6"
            value={infectionForm.temp}
            onChange={(e) => setInfectionForm({ ...infectionForm, temp: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
            data-testid="infection-temp-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">HR</Label>
          <Input
            type="number"
            placeholder="72"
            value={infectionForm.hr}
            onChange={(e) => setInfectionForm({ ...infectionForm, hr: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">BP</Label>
          <Input
            placeholder="120/80"
            value={infectionForm.bp}
            onChange={(e) => setInfectionForm({ ...infectionForm, bp: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Urinary Symptoms</Label>
        <div className="flex flex-wrap gap-2">
          {URINARY_SYMPTOMS.map((symptom) => (
            <Badge
              key={symptom}
              variant={infectionForm.urinary_symptoms.includes(symptom) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => {
                if (infectionForm.urinary_symptoms.includes(symptom)) {
                  setInfectionForm({
                    ...infectionForm,
                    urinary_symptoms: infectionForm.urinary_symptoms.filter(s => s !== symptom)
                  });
                } else {
                  setInfectionForm({
                    ...infectionForm,
                    urinary_symptoms: [...infectionForm.urinary_symptoms, symptom]
                  });
                }
              }}
            >
              {symptom}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Respiratory Symptoms</Label>
        <div className="flex flex-wrap gap-2">
          {RESPIRATORY_SYMPTOMS.map((symptom) => (
            <Badge
              key={symptom}
              variant={infectionForm.respiratory_symptoms.includes(symptom) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => {
                if (infectionForm.respiratory_symptoms.includes(symptom)) {
                  setInfectionForm({
                    ...infectionForm,
                    respiratory_symptoms: infectionForm.respiratory_symptoms.filter(s => s !== symptom)
                  });
                } else {
                  setInfectionForm({
                    ...infectionForm,
                    respiratory_symptoms: [...infectionForm.respiratory_symptoms, symptom]
                  });
                }
              }}
            >
              {symptom}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Clinical Notes</Label>
        <Textarea
          placeholder="Additional observations about potential infection..."
          value={infectionForm.clinical_notes}
          onChange={(e) => setInfectionForm({ ...infectionForm, clinical_notes: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={3}
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading || infectionForm.signs_symptoms.length === 0}
        className="w-full bg-gradient-to-r from-orange-600 to-red-600"
        data-testid="submit-infection-assessment-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Thermometer className="h-4 w-4 mr-2" />}
        Analyze Infection Assessment
      </Button>
    </div>
  );
}

export function AssessmentResultDisplay({ result, isDark }) {
  if (!result) return null;

  const { type, data } = result;

  return (
    <Card className={`mt-4 ${isDark ? "bg-slate-800/80 border-cyan-500/30" : "bg-cyan-50 border-cyan-200"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          {type.charAt(0).toUpperCase() + type.slice(1)} Assessment Result
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-64">
          {data.severity && (
            <div className="mb-3">
              <Badge className={getSeverityClass(data.severity)}>
                Severity: {data.severity}
              </Badge>
            </div>
          )}

          {data.documentation_prompts && data.documentation_prompts.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1">Documentation Prompts:</p>
              <ul className="text-xs space-y-1">
                {data.documentation_prompts.map((prompt, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-cyan-400">•</span>
                    {prompt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.recommendations && data.recommendations.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1">Recommendations:</p>
              <ul className="text-xs space-y-1">
                {data.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-green-400">✓</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.alerts && data.alerts.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1 text-red-400">Alerts:</p>
              {data.alerts.map((alert, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  {alert.message}
                </div>
              ))}
            </div>
          )}

          {/* Pain-specific fields */}
          {type === "pain" && (
            <>
              {data.pain_trend && (
                <p className="text-xs mb-2">
                  <span className="font-semibold">Trend:</span> {data.pain_trend}
                </p>
              )}
              {data.reassessment_timeline && (
                <p className="text-xs mb-2">
                  <span className="font-semibold">Reassess:</span> {data.reassessment_timeline}
                </p>
              )}
            </>
          )}

          {/* Infection-specific fields */}
          {type === "infection" && data.infection_type && (
            <p className="text-xs mb-2">
              <span className="font-semibold">Suspected Type:</span> {data.infection_type}
            </p>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
